import { observer } from 'mobx-react-lite'
import { Plus, Trophy } from 'iconoir-react'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeader } from 'app/components/PageHeader'
import DateRangeSelector from 'app/components/scorecards/DateRangeSelector'
import { LocationSelector } from 'app/components/scorecards/LocationSelector'
import LookbackAmountSelector from 'app/components/scorecards/LookbackAmountSelector'
import LookbackTypeSelector from 'app/components/scorecards/LookbackTypeSelector'
import { StaffSelector } from 'app/components/scorecards/StaffSelector'
import Spinner from 'app/components/Spinner'
import SimpleTable from 'app/components/table/SimpleTable'
import type { UseScorecardsReturn } from './useScorecardsPage'

export type ScorecardsComponentProps = UseScorecardsReturn

export const ScorecardsComponent = observer(function ScorecardsComponent(props: ScorecardsComponentProps) {
  const {
    activeScorecard,
    locations,
    setLocations,
    staff,
    setStaff,
    dateRange,
    lookbackType,
    setLookbackType,
    lookbackAmount,
    setLookbackAmount,
    rows,
    columnVisibility,
    columnOrder,
    sort,
    isLoading,
    savable,
    columns,
    onCreateScorecardClick,
    onSaveChangesClick,
    handleColumnVisibilityChange,
    handleColumnOrderChange,
    handleSortChange,
    resetToDefaults,
    handleDateRangeChange,
  } = props

  return (
    <div className="flex h-full flex-col">
      {activeScorecard ? (
        <PageScroll
          variant="table"
          header={
            <PageHeader
              title={activeScorecard.name!}
              primaryButtonSlot={
                <button disabled={!savable} className="btn btn-primary btn-md" onClick={() => onSaveChangesClick()}>
                  Save Changes
                </button>
              }
              secondaryButtonSlot={
                <button className="btn btn-secondary btn-md" onClick={() => resetToDefaults()}>
                  Reset
                </button>
              }
            />
          }
        >
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner size={20} />
            </div>
          ) : (
            <SimpleTable
              extraSlot={
                <>
                  {activeScorecard.grouping === `Location` && (
                    <LocationSelector
                      selectedLocations={locations}
                      onLocationsChange={setLocations}
                      inputClassName="flex items-center !shadow-none !rounded-md text-body-xs text-strong !h-8 !py-[9px]"
                    />
                  )}
                  {activeScorecard.grouping !== `Staff` && (
                    <StaffSelector
                      selectedStaff={staff}
                      onStaffChange={setStaff}
                      inputClassName="flex items-center !shadow-none !rounded-md text-body-xs text-strong !h-8 !py-[9px]"
                    />
                  )}
                  <DateRangeSelector
                    currentValue={dateRange}
                    onChange={handleDateRangeChange}
                    inputClassName="flex items-center !shadow-none !rounded-md text-body-xs text-strong !h-8 !py-[9px]"
                  />
                  <LookbackTypeSelector
                    currentDateRange={dateRange}
                    currentValue={lookbackType}
                    onChange={setLookbackType}
                    inputClassName="flex items-center !shadow-none !rounded-md text-body-xs text-strong !h-8 !py-[9px]"
                  />
                  <LookbackAmountSelector
                    currentValue={lookbackAmount}
                    onChange={setLookbackAmount}
                    currentDateRange={dateRange}
                    currentLookbackType={lookbackType}
                    inputClassName="flex items-center !shadow-none !rounded-md text-body-xs text-strong !h-8 !py-[9px]"
                  />
                </>
              }
              columns={columns as any}
              data={rows}
              initialSort={activeScorecard.sort}
              syncedSort={sort}
              onSortChange={handleSortChange}
              showColumnOrdering={true}
              syncedColumnVisibility={columnVisibility}
              onColumnVisibilityChange={handleColumnVisibilityChange}
              syncedColumnOrder={columnOrder}
              onColumnOrderChange={handleColumnOrderChange}
              activeSubpage={activeScorecard.id}
              hideFooter={true}
              meta={{ getRowStyle: (row, depth) => (depth === 0 ? `table-parent-row` : `table-child-row`) }}
              edgePadded
            />
          )}
        </PageScroll>
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center">
          <Trophy width={60} height={60} className="text-default" />
          <p className="mt-6 text-heading-xl text-strong">Create your first Scorecard</p>
          <p className="mt-4 text-center text-body-md text-default">You have no scorecards created.</p>
          <button className="mt-8 flex items-center px-6 py-3" onClick={() => onCreateScorecardClick()}>
            <Plus width={16} height={16} className="text-default" />
            <p className="ml-2 text-ui-sm text-default">Create Scorecard</p>
          </button>
        </div>
      )}
    </div>
  )
})
